'use client';
import React, { useEffect, useRef, useState } from 'react';

const whyItems = [
  {
    emoji: '🌍',
    title: 'Planet-First Philosophy',
    description: 'Every product we make is designed with the environment in mind. No virgin plastic, no compromise.',
  },
  {
    emoji: '🎨',
    title: 'Custom Branding',
    description: 'Your logo, your colors, your story on every box, bag, and label we produce.',
  },
  {
    emoji: '📦',
    title: 'MOQ of Just 100 Units*',
    description: 'Start small, scale big. Our low minimum order quantity makes custom packaging accessible to every brand.',
  },
  {
    emoji: '⚡',
    title: 'Fast Turnaround',
    description: 'Standard orders ship in 3–5 business days. Custom orders typically ready in 2–3 weeks.',
  },
  {
    emoji: '🔍',
    title: 'Quality Assured',
    description: 'Tree-free paper materials and rigorous quality checks on every order.',
  },
  {
    emoji: '🤝',
    title: 'Dedicated Support',
    description: 'Our packaging experts guide you from concept to delivery, every step of the way.',
  },
];

export default function WhyEcosange() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 lg:py-20 px-6 lg:px-12 max-w-7xl mx-auto" id="about">
      <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-10">
          <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
            Why Ecosange
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-primary-dark"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Packaging that cares for the planet
          </h2>
          <p className="text-gray-500 font-normal mt-3 max-w-xl mx-auto">
            Conscious packaging that can be recycled, reused, or compostable — this is the new normal.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyItems?.map((item, i) => (
            <div
              key={item?.title}
              className="group p-6 rounded-2xl border border-gray-100 hover:border-primary/30 hover:shadow-card transition-all duration-300 bg-white"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                <span className="text-2xl">{item?.emoji}</span>
              </div>
              <h3
                className="text-base font-semibold text-gray-800 mb-2"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {item?.title}
              </h3>
              <p className="text-sm text-gray-500 font-normal leading-relaxed">{item?.description}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 text-center mt-4 font-normal">* MOQ of 100 units applies to standard products. Custom product MOQ may vary.</p>
      </div>
    </section>
  );
}
